// Follower-tab take-over UI (docs/06-clients.md §4): the leader renders the
// app normally; followers see "Merkury is active in another tab" with a
// take-over button. Wired around <App/> by the shell at integration time.

import { useSyncExternalStore, type ReactNode } from 'react';
import type { TabElection, TabElectionState } from './election.js';

export function useTabElection(election: TabElection): TabElectionState {
  return useSyncExternalStore(
    (listener) => election.state.subscribe(listener),
    () => election.state.get(),
  );
}

export function MasterTabGate({
  election,
  children,
}: {
  election: TabElection;
  children: ReactNode;
}): ReactNode {
  const { role, takingOver } = useTabElection(election);
  // 'pending' resolves within a tick (ifAvailable probe); rendering children
  // immediately avoids a flash of take-over UI in the common single-tab case.
  if (role !== 'follower') return children;
  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col items-center justify-center gap-6 p-6 text-center">
      <h1 className="text-xl font-semibold">Merkury is active in another tab</h1>
      <p className="text-sm text-muted">
        Calls ring in one tab at a time so you never get double audio.
      </p>
      <button
        type="button"
        disabled={takingOver}
        onClick={() => election.takeOver()}
        className="min-h-touch rounded-xl bg-accent px-6 font-medium text-ink disabled:opacity-40 active:bg-accent-press"
      >
        {takingOver ? 'Taking over…' : 'Use Merkury here'}
      </button>
    </main>
  );
}
